/**
 * Spec slash command definitions for mia-flow chat.
 * Based on https://github.com/github/spec-kit
 *
 * These commands implement the Spec-Driven Development workflow:
 * constitution → specify → clarify → plan → tasks → analyze → implement
 */

export const SPECKIT_COMMANDS = [
  {
    command: '/spec.constitution',
    label: 'constitution',
    icon: '📜',
    description: 'Create or update project governing principles and development guidelines',
    argHint: '[focus areas, e.g. "code quality, testing standards"]',
    phase: 'core',
  },
  {
    command: '/spec.specify',
    label: 'specify',
    icon: '📝',
    description: 'Define what to build – requirements and user stories (focus on WHAT and WHY)',
    argHint: '<feature description>',
    phase: 'core',
  },
  {
    command: '/spec.clarify',
    label: 'clarify',
    icon: '❓',
    description: 'Clarify underspecified areas before planning (run after /spec.specify)',
    argHint: '[optional context]',
    phase: 'optional',
  },
  {
    command: '/spec.plan',
    label: 'plan',
    icon: '🗺️',
    description: 'Create a technical implementation plan with your chosen tech stack',
    argHint: '<tech stack and architecture choices>',
    phase: 'core',
  },
  {
    command: '/spec.tasks',
    label: 'tasks',
    icon: '✅',
    description: 'Generate an actionable, dependency-ordered task list from the plan',
    argHint: '[optional context]',
    phase: 'core',
  },
  {
    command: '/spec.analyze',
    label: 'analyze',
    icon: '🔍',
    description: 'Cross-artifact consistency & coverage analysis (run after tasks, before implement)',
    argHint: '[optional context]',
    phase: 'optional',
  },
  {
    command: '/spec.checklist',
    label: 'checklist',
    icon: '📋',
    description: 'Generate custom quality checklists that validate requirements completeness',
    argHint: '<domain, e.g. "security review" or "API design">',
    phase: 'optional',
  },
  {
    command: '/spec.implement',
    label: 'implement',
    icon: '🚀',
    description: 'Execute all tasks and build the feature according to the plan',
    argHint: '[optional: start from phase N]',
    phase: 'core',
  },
  {
    command: '/spec.memory',
    label: 'memory',
    icon: '🧠',
    description: 'Update persistent project memory with finalized decisions and constraints',
    argHint: '[what to record, e.g. "we chose PostgreSQL because..."]',
    phase: 'optional',
  },
];

/** Commands that form the core linear workflow */
export const SPECKIT_CORE_COMMANDS = SPECKIT_COMMANDS.filter(c => c.phase === 'core');

/** Suggested follow-up commands keyed by the current command */
export const SPECKIT_NEXT_STEPS = {
  '/spec.constitution': ['/spec.specify'],
  '/spec.specify': ['/spec.clarify', '/spec.plan'],
  '/spec.clarify': ['/spec.plan'],
  '/spec.plan': ['/spec.tasks', '/spec.checklist'],
  '/spec.tasks': ['/spec.analyze', '/spec.implement'],
  '/spec.analyze': ['/spec.implement'],
  '/spec.checklist': ['/spec.implement'],
  '/spec.implement': ['/spec.memory'],
  '/spec.memory': [],
};

/**
 * Return the matching command entry for a given input string (prefix match).
 * @param {string} input
 * @returns {import('./speckitCommands').SpecCommand | null}
 */
export function matchSpeckitCommand(input) {
  if (!input || !input.startsWith('/spec')) return null;
  const lower = input.toLowerCase();
  return SPECKIT_COMMANDS.find(c => lower.startsWith(c.command.toLowerCase())) || null;
}

/**
 * Return all commands whose command string starts with the given prefix.
 * Used for the autocomplete menu.
 * @param {string} prefix
 * @returns {Array}
 */
export function filterSpeckitCommands(prefix, dynamicList) {
  const list = dynamicList || SPECKIT_COMMANDS;
  if (!prefix) return list;
  const lower = prefix.toLowerCase();
  return list.filter(c => c.command.toLowerCase().startsWith(lower));
}

/**
 * Build a slash-command list from catalog ai-spec items.
 * Each item must have { command, name, description } at minimum.
 * @param {Array} specs
 * @returns {Array}
 */
export function buildCommandsFromSpecs(specs) {
  if (!specs || !specs.length) return [];
  return specs
    .filter(s => s.command)
    .map(s => ({
      command: `/spec.${s.command}`,
      label: s.command,
      icon: '📋',
      description: s.description || s.name || s.command,
      argHint: '',
      phase: 'catalog',
    }));
}
